import type { ReactElement, ReactNode } from 'react';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled, { keyframes } from 'styled-components';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Header title. Omit (with no `headerExtra`) to render no header at all. */
  title?: string;
  /** Optional content under the title row (e.g. a search bar). */
  headerExtra?: ReactNode;
  /** Optional footer content (buttons, counts, …). */
  footer?: ReactNode;
  /** Container width in px (default 520). */
  width?: number;
  children: ReactNode;
}

const modalIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px) scale(0.98);
  }

  to {
    opacity: 1;
    transform: none;
  }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 200;
  background: rgba(6, 10, 22, 0.62);
  backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const Container = styled.div<{ $width: number }>`
  width: ${({ $width }): string => `${$width}px`};
  max-width: 100%;
  max-height: 82vh;
  display: flex;
  flex-direction: column;
  background: rgb(15, 24, 48);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  box-shadow: rgba(0, 0, 0, 0.6) 0px 30px 80px;
  animation: ${modalIn} 0.22s cubic-bezier(0.22, 1, 0.36, 1);
  overflow: hidden;
`;

const Header = styled.div`
  padding: 20px 22px 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);

  .top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }

  .title {
    font-size: 16px;
    font-weight: 700;
    color: rgb(255, 255, 255);
  }

  .extra {
    margin-top: 13px;
  }
`;

const CloseButton = styled.button`
  display: inline-grid;
  place-items: center;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: rgba(213, 221, 238, 0.85);
  cursor: pointer;
  flex-shrink: 0;
  transition: background var(--transition);

  &:hover,
  &:focus {
    outline: none;
    background: rgba(255, 255, 255, 0.1) !important;
  }
`;

const Body = styled.div`
  flex: 1 1 0%;
  overflow-y: auto;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 7px;
`;

const Footer = styled.div`
  padding: 14px 22px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
`;

/*
 * Reusable modal shell: overlay + animated card with a header (title + close,
 * plus optional `headerExtra`), a scrollable body (children), and an optional
 * footer. Rendered through a portal so it escapes any clipping ancestor.
 */
export const Modal = ({
  open,
  onClose,
  title,
  headerExtra,
  footer,
  width = 520,
  children
}: Props): ReactElement | null => {
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (open) {
      document.addEventListener('keydown', onKey);
    }

    return (): void => {
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  return createPortal(
    <Overlay onClick={onClose}>
      <Container
        $width={width}
        onClick={(event): void => {
          event.stopPropagation();
        }}
      >
        {(title !== undefined || headerExtra !== undefined) && (
          <Header>
            {title !== undefined && (
              <div className='top'>
                <div className='title'>{title}</div>
                <CloseButton type='button' aria-label='Close' onClick={onClose}>
                  <i className='fal fa-times' />
                </CloseButton>
              </div>
            )}
            {headerExtra !== undefined && <div className='extra'>{headerExtra}</div>}
          </Header>
        )}

        <Body className='cc-scroll'>{children}</Body>

        {footer !== undefined && <Footer>{footer}</Footer>}
      </Container>
    </Overlay>,
    document.body
  );
};
